'use client';

import { useEffect, useRef, useState } from 'react';
import { AccountDeletionApiError, confirmAccountDeletion, requestAccountDeletionCode } from './account-deletion.api';
import { ACCOUNT_DELETION_CODE_LENGTH, ACCOUNT_DELETION_NEUTRAL_MESSAGE, DEFAULT_RETRY_SECONDS, EMPTY_DELETION_IDENTITY } from './account-deletion.constants';
import { normalizeDeletionIdentity, remainingSeconds, validateDeletionIdentity } from './account-deletion.helpers';
import type { ActiveDeletionChallenge, DeletionIdentity, DeletionIdentityErrors, DeletionNotice, DeletionResult } from './account-deletion.types';
export function useAccountDeletion() {
  const [identity, setIdentity] = useState(EMPTY_DELETION_IDENTITY);
  const [errors, setErrors] = useState<DeletionIdentityErrors>({});
  const [challenge, setChallenge] = useState<ActiveDeletionChallenge | null>(null);
  const [code, setCode] = useState('');
  const [pending, setPending] = useState<'request' | 'confirm' | null>(null);
  const [notice, setNotice] = useState<DeletionNotice | null>(null);
  const [result, setResult] = useState<DeletionResult | null>(null);
  const [requestBlockedUntil, setRequestBlockedUntil] = useState(0);
  const [confirmBlockedUntil, setConfirmBlockedUntil] = useState(0);
  const [now, setNow] = useState(Date.now);
  const activeRequest = useRef<AbortController | null>(null);
  const confirming = useRef(false);
  useEffect(() => () => activeRequest.current?.abort(), []);
  const hasActiveDeadline = (challenge?.expiresAt ?? 0) > now || requestBlockedUntil > now || confirmBlockedUntil > now;
  useEffect(() => {
    if (!hasActiveDeadline) return;
    const refreshTime = () => setNow(Date.now());
    const timer = window.setInterval(refreshTime, 1000);
    document.addEventListener('visibilitychange', refreshTime);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', refreshTime);
    };
  }, [hasActiveDeadline]);
  const invalidateChallenge = () => {
    activeRequest.current?.abort();
    activeRequest.current = null;
    setPending(null);
    setChallenge(null);
    setCode('');
  };
  const editDetails = () => {
    if (confirming.current) return;
    invalidateChallenge();
    setNotice(null);
    setNow(Date.now());
  };
  const updateIdentity = <Key extends keyof DeletionIdentity,>(field: Key, value: DeletionIdentity[Key]) => {
    if (confirming.current) return;
    editDetails();
    setErrors({});
    setIdentity(current => ({
      ...current,
      [field]: value
    }));
  };
  const showFailure = (error: unknown, action: 'request' | 'confirm') => {
    if (error instanceof AccountDeletionApiError && error.status === 429) {
      const seconds = error.retryAfterSeconds ?? DEFAULT_RETRY_SECONDS;
      const deadline = Date.now() + seconds * 1000;
      setRequestBlockedUntil(current_0 => Math.max(current_0, deadline));
      if (action === 'confirm') {
        setConfirmBlockedUntil(current_1 => Math.max(current_1, deadline));
      }
      setNow(Date.now());
      setNotice({
        kind: 'error',
        text: `Too many attempts. Please try again in ${seconds} seconds.`
      });
      return;
    }
    if (error instanceof AccountDeletionApiError && action === 'confirm') {
      if (error.status === 400 || error.status === 409) {
        setChallenge(null);
        setCode('');
        setNotice({
          kind: 'error',
          text: error.status === 400 ? 'Code invalid or expired' : error.message
        });
        return;
      }
    }
    setNotice({
      kind: 'error',
      text: error instanceof AccountDeletionApiError ? (error.status ?? 0) >= 500 ? 'The service is temporarily unavailable. Please try again.' : action === 'request' ? 'Unable to request a code. Please try again or contact support.' : 'Unable to confirm account deletion. Please try again or contact support.' : 'Unable to connect. Please check your connection and try again.'
    });
  };
  const requestCode = async () => {
    const currentTime = Date.now();
    if (activeRequest.current || result || currentTime < requestBlockedUntil || currentTime < (challenge?.resendAt ?? 0)) return;
    const normalizedIdentity = normalizeDeletionIdentity(identity);
    const nextErrors = validateDeletionIdentity(normalizedIdentity);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;
    const controller = new AbortController();
    activeRequest.current = controller;
    setPending('request');
    setNotice(null);
    setCode('');
    try {
      const requestedChallenge = await requestAccountDeletionCode(normalizedIdentity, controller.signal);
      if (controller.signal.aborted || activeRequest.current !== controller) return;
      const receivedAt = Date.now();
      setNow(receivedAt);
      setIdentity(normalizedIdentity);
      setRequestBlockedUntil(current_2 => Math.max(current_2, receivedAt + requestedChallenge.resendAfterSeconds * 1000));
      setChallenge({
        ...requestedChallenge,
        expiresAt: receivedAt + requestedChallenge.expiresInSeconds * 1000,
        resendAt: receivedAt + requestedChallenge.resendAfterSeconds * 1000
      });
      setNotice({
        kind: 'info',
        text: ACCOUNT_DELETION_NEUTRAL_MESSAGE
      });
    } catch (error_0) {
      if (!controller.signal.aborted && activeRequest.current === controller) {
        showFailure(error_0, 'request');
      }
    } finally {
      if (!controller.signal.aborted && activeRequest.current === controller) {
        activeRequest.current = null;
        setPending(null);
      }
    }
  };
  const deleteAccount = async () => {
    const currentTime_0 = Date.now();
    if (activeRequest.current || result || !challenge || currentTime_0 < confirmBlockedUntil) return;
    if (currentTime_0 >= challenge.expiresAt) {
      setNow(currentTime_0);
      setNotice({
        kind: 'error',
        text: 'Code invalid or expired'
      });
      return;
    }
    if (!new RegExp(`^\\d{${ACCOUNT_DELETION_CODE_LENGTH}}$`, 'u').test(code)) {
      setNotice({
        kind: 'error',
        text: 'Enter the six-digit verification code.'
      });
      return;
    }
    const controller_0 = new AbortController();
    activeRequest.current = controller_0;
    setPending('confirm');
    confirming.current = true;
    setNotice(null);
    try {
      const deletionResult = await confirmAccountDeletion(challenge.challengeId, code, controller_0.signal);
      if (controller_0.signal.aborted || activeRequest.current !== controller_0) return;
      setResult(deletionResult);
      setIdentity(EMPTY_DELETION_IDENTITY);
      setChallenge(null);
      setCode('');
    } catch (error_1) {
      if (!controller_0.signal.aborted && activeRequest.current === controller_0) {
        showFailure(error_1, 'confirm');
      }
    } finally {
      if (!controller_0.signal.aborted && activeRequest.current === controller_0) {
        activeRequest.current = null;
        setPending(null);
        confirming.current = false;
      }
    }
  };
  const expiresIn = remainingSeconds(challenge?.expiresAt ?? 0, now);
  const resendIn = remainingSeconds(Math.max(challenge?.resendAt ?? 0, requestBlockedUntil), now);
  const confirmRetryIn = remainingSeconds(confirmBlockedUntil, now);
  return {
    identity,
    updateIdentity,
    errors,
    challenge,
    code,
    setCode,
    pending,
    notice,
    result,
    requestCode,
    deleteAccount,
    editDetails,
    expiresIn,
    resendIn,
    confirmRetryIn,
    canRequest: !pending && resendIn === 0,
    canConfirm: !pending && expiresIn > 0 && confirmRetryIn === 0 && code.length === ACCOUNT_DELETION_CODE_LENGTH
  };
}